import styled from '@emotion/styled';
import { captureException } from '@sentry/minimal';
import { Action } from 'history';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useEffect, useState } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { navigationService } from 'src/config/rootService';

import { AudioPage } from 'src/modules/newPrograms/components/AudioPage';
import { MarkdownPage } from 'src/modules/newPrograms/components/MarkdownPage';
import { ProgressBar } from 'src/modules/newPrograms/components/ProgressBar';
import { RateProgramPage } from 'src/modules/newPrograms/components/RateProgramPage';
import { SelectPage } from 'src/modules/newPrograms/components/SelectPage';
import { TextInputPage } from 'src/modules/newPrograms/components/TextInputPage';
import { VideoPage } from 'src/modules/newPrograms/components/VideoPage';
import { WelcomePage } from 'src/modules/newPrograms/components/WelcomePage';
import { ProgramWrapper, useProgramContext } from 'src/modules/newPrograms/contexts/programContext';
import {
    ProgramFragment,
    ProgramPageFragment,
    useGetProgramWithPagesBySlugQuery,
} from 'src/modules/newPrograms/documents/getProgram.generated';
import { NewPageTypes, PageConversion } from 'src/modules/newPrograms/models/program';
import closeIcon from 'src/public/img/close-icon.svg';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';
import { color, layout } from 'src/shared/styles/variables';

import { EarlyExitFeedbackModal } from '../components/EarlyExitFeedbackModal';
import { PageBackground } from '../components/PageBackground';

const Container = styled.div({
    padding: '0px 1.5em',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    width: '100%',
    alignSelf: 'center',
    position: 'relative',
});

const ContentContainer = styled.div({
    maxWidth: layout.containerWidth,
    width: '100%',
    margin: '1.75rem',
    alignSelf: 'center',
    justifySelf: 'flex-start',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
});

const ExitContainer = styled.div({
    height: 32,
    width: 32,
    border: `2px solid ${color.catskillWhite}`,
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'flex-end',
    marginTop: 12,
    cursor: 'pointer',
    img: {
        width: 8,
    },
});

interface Props {
    program: ProgramFragment;
    page: ProgramPageFragment;
    shouldShowRatingPage: boolean;
    navigateToNext: () => void;
    navigateToPrevious: () => void;
}

export const ProgramPage: React.FC<Props> = ({
    page,
    program,
    navigateToNext,
    shouldShowRatingPage,
    navigateToPrevious,
}) => {
    const { isFirstPage, finished } = useProgramContext();
    const featureFlags = useFlags();
    const history = useHistory();
    const [navigationAttemptDetails, setNavigationAttemptDetails] = useState<{
        pathname: string;
        action: Action;
    } | null>(null);

    // this should prevent users from navigating out of the program if early exit feedback is enabled
    // any navigate out attempt should result in showing early exit feedback modal
    // pressing on the skip button in the modal should take the user to where he initially wanted to go
    // we're keeping this information in the `navigationAttemptDetails`
    useEffect(() => {
        const unblock = history.block((location, action) => {
            // block navigate out attempt and show early exit feedback modal
            if (
                featureFlags.showProgramsEarlyExitFeedback &&
                !finished &&
                !navigationAttemptDetails
            ) {
                setNavigationAttemptDetails({ pathname: location.pathname, action });

                return false;
            }

            // allow navigate event to go through
            return;
        });

        return () => {
            unblock();
        };
    }, [finished, featureFlags.showProgramsEarlyExitFeedback, navigationAttemptDetails]);

    const handleEarlyExitFeedbackClose = () => {
        switch (navigationAttemptDetails?.action) {
            case 'PUSH':
                history.push(navigationAttemptDetails.pathname);
                break;
            case 'REPLACE':
                history.replace(navigationAttemptDetails.pathname);
                break;
            case 'POP':
                history.goBack();
                break;
            default:
                history.goBack();
        }
    };

    const commonProps = {
        onNavigatePrev: () => navigateToPrevious(),
        onNavigateNext: () => navigateToNext(),
    };

    const type = PageConversion[page.type];
    const isAudioPage = type === NewPageTypes.Audio && page.audioFile;

    let ProgramPageComponent = <MarkdownPage page={page} {...commonProps} />;

    if (isFirstPage) {
        ProgramPageComponent = <WelcomePage page={page} onClick={() => navigateToNext()} />;
    }

    if (shouldShowRatingPage) {
        ProgramPageComponent = <RateProgramPage program={program} />;
    }

    if (isAudioPage) {
        ProgramPageComponent = <AudioPage page={page} {...commonProps} />;
    }

    if (type === NewPageTypes.MultiSelectQuestion) {
        ProgramPageComponent = <SelectPage page={page} {...commonProps} />;
    }

    if (type === NewPageTypes.SingleSelectQuestion) {
        ProgramPageComponent = <SelectPage page={page} {...commonProps} />;
    }

    if (type === NewPageTypes.TextInputQuestion) {
        ProgramPageComponent = <TextInputPage page={page} {...commonProps} />;
    }

    if (type === NewPageTypes.Video) {
        ProgramPageComponent = <VideoPage page={page} {...commonProps} />;
    }

    return (
        <>
            {ProgramPageComponent}
            <EarlyExitFeedbackModal
                visible={!!navigationAttemptDetails}
                onContinueProgram={() => setNavigationAttemptDetails(null)}
                onExit={handleEarlyExitFeedbackClose}
                programUuid={program.uuid}
            />
        </>
    );
};

const preloadImage = (src?: string) => {
    if (!src) {
        return;
    }

    const image = new Image();
    image.src = src;
};

export const ProgramState: React.FC<{}> = () => {
    const { t } = useTranslation();

    const {
        finished: programFinished,
        currentPageNumber: pageNumber,
        currentPage: page,
        completionPercent,
        program,
        nextPage,
        previousPage,
    } = useProgramContext();

    const pages = useMemo(() => program?.pages, [program]);

    useEffect(() => {
        if (program) {
            document.title = t('programs:page.title', { programName: program.name });
        }

        return () => {
            document.title = t('shared:page.title');
        };
    }, [t, program]);

    useEffect(() => {
        const keysToPreload = ['firstBackgroundImageUrl', 'secondBackgroundImageUrl', 'image'];

        if (pages) {
            pages.forEach(page => keysToPreload.forEach(key => preloadImage(page?.[key])));
        }
    }, [pages]);

    if (!page && (!program.showRatings || !programFinished)) {
        // Log page navigation error to Sentry.
        captureException(
            new Error(`Failed to navigate to page ${pageNumber} in program ${program.uuid}.`),
        );

        // If a page doesn't exist, we can redirect to the programs page for now.
        return <Redirect to={`/programs`} />;
    }

    if (!program || !page) {
        return <CenteredSpinner />;
    }

    const shouldShowRatingPage = !!programFinished && !!program.showRatings;

    return (
        <Container>
            <ContentContainer>
                {shouldShowRatingPage ? null : (
                    <ProgressBar progressPercentage={completionPercent} />
                )}
                {shouldShowRatingPage ? null : (
                    <ExitContainer onClick={() => navigationService.goBack()}>
                        <img src={closeIcon} alt="exit from program icon" />
                    </ExitContainer>
                )}
                <ProgramPage
                    program={program}
                    page={page}
                    shouldShowRatingPage={shouldShowRatingPage}
                    navigateToNext={() => nextPage()}
                    navigateToPrevious={() => previousPage()}
                />
            </ContentContainer>
            <PageBackground
                backgroundImageUrl={page.firstBackgroundImageUrl ?? ''}
                backgroundImagePosition={page.firstBackgroundImagePosition}
            />
            <PageBackground
                backgroundImageUrl={page.secondBackgroundImageUrl ?? ''}
                backgroundImagePosition={page.secondBackgroundImagePosition}
            />
        </Container>
    );
};

interface Params {
    slug: string;
}

export const Program: React.FC = () => {
    const { slug } = useParams<Params>();

    const { data, loading, error } = useGetProgramWithPagesBySlugQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            slug,
        },
    });

    if (loading) {
        return <CenteredSpinner />;
    }

    if (error) {
        captureException(error);

        // TODO(evanmh): Consider error state.
        return <Redirect to="/programs" />;
    }

    if (!data?.programBySlug) {
        // TODO(evanmh): Consider not found state.
        return <Redirect to="/programs" />;
    }

    return (
        <ProgramWrapper program={data.programBySlug}>
            <ProgramState />
        </ProgramWrapper>
    );
};
